import Link from "next/link";
import { FC } from "react";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { Menu } from "@headlessui/react";

import { CategoryWithProductCount } from "@interfaces/categories";
import FilterMenu from "@components/products/filters/filter-menu";
import { classNames } from "@utils/class-names";

interface Props {
  categories?: CategoryWithProductCount[];
}

const MobileSubCategories: FC<Props> = ({ categories }) => {
  const { t } = useTranslation("common");
  const {
    query: {
      slug: [parentCategory, childCategory],
    },
    locale,
  } = useRouter();

  return categories && !!categories.length ? (
    <div className="w-full flex items-center justify-center">
      <FilterMenu
        {...{ text: t("sub-category") }}
        className="max-h-[60vh] overflow-y-scroll"
      >
        {categories.map((current) => (
          <Menu.Item
            key={current.slug}
            disabled={current.slug === childCategory}
          >
            {({ active, disabled }) => (
              <Link
                href={{
                  pathname: "/categories/[...slug]",
                  query: { slug: [parentCategory, current.slug] },
                }}
              >
                <a
                  className={`text-left py-2 px-4 w-full transition-all duration-200
                ${active ? "bg-gray-400" : ""}
                ${disabled ? "text-gray-400" : ""}`}
                >
                  {t(locale === "en" ? current.name_en : current.name)}
                </a>
              </Link>
            )}
          </Menu.Item>
        ))}
      </FilterMenu>
    </div>
  ) : null;
};

const SubCategories: FC<Props> = ({ categories }) => {
  const router = useRouter();
  const { slug } = router.query;

  if (!categories || !categories.length) {
    return null;
  }

  const [parentCategory, childCategory] = slug;

  return (
    <div>
      <div className="md:hidden">
        <MobileSubCategories {...{ categories }} />
      </div>
      <div className="w-full px-10 justify-center flex-wrap hidden md:flex">
        {categories &&
          !!categories.length &&
          categories.map(
            ({ slug: categorySlug, name, name_en, product_count }) => (
              <div key={categorySlug}>
                <Link
                  href={{
                    pathname: "/categories/[...slug]",
                    query: { slug: [parentCategory, categorySlug] },
                  }}
                >
                  <a
                    className={classNames(
                      "flex justify-between m-2 p-2 border border-black rounded bg-white hover:bg-black hover:text-white transition-colors duration-200",
                      categorySlug === childCategory ? "bg-gray-600" : ""
                    )}
                  >
                    <h5 className="font-semibold">
                      {router.locale === "en" ? name_en : name}
                    </h5>
                    <span className="font-light pl-2">({product_count})</span>
                  </a>
                </Link>
              </div>
            )
          )}
      </div>
    </div>
  );
};

export default SubCategories;
